import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  verifyInvitation,
  registerMemberStep1,
  registerVerifyPhone,
  registerSignConsent,
  registerComplete,
  getActiveConsentDocuments,
  registerUpdatePhone,
  registerResendCode,
} from '@/lib/memberAuth';
import { AlertCircle, CheckCircle, UserPlus, Lock, Phone, Mail, Shield, FileText } from 'lucide-react';
import PhoneInput from '@/components/ui/PhoneInput';
import { digitsOnly } from '@/utils/phoneFormat';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { parseInviteCode } from '@/lib/memberJoin';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';
import { DOOR_INPUT, DOOR_PRIMARY, HelpLineFooter } from '@/components/member-portal/MemberDoorChrome';

// TRUS-735: a US mobile is 10 digits, or 11 with a leading 1. The server
// refuses anything else; checking here saves a round trip and a dead SMS.
function isValidUsMobile(value: string): boolean {
  const digits = digitsOnly(value);
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

/**
 * /member/register and /member/register/:token. Without a token (someone who
 * was told "use your invite code", or tapped "I have an invite code") show a
 * form for the code or the link, instead of "No invitation token provided".
 */
export default function MemberRegisterRoute() {
  const { token } = useParams<{ token: string }>();
  return token ? <MemberRegisterPage /> : <InviteCodeEntry />;
}

function InviteCodeEntry() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = parseInviteCode(value);
    if (!code) {
      setError(t('register.invite_not_recognised', 'We could not read that code or link. Check it and try again.'));
      return;
    }
    navigate(`/member/register/${encodeURIComponent(code)}`);
  };

  return (
    <div className={`${AUTH_BACKDROP} flex flex-col`}>
      <header className="bg-surface border-b border-v2border">
        <div className="max-w-xl w-full mx-auto px-4 py-3">
          <Link to="/member/home" aria-label={t('register.home', 'WeaveIn home')}>
            <img src="/weavein-wordmark.png" alt="WeaveIn" className="h-8 lg:h-10 xl:h-11 w-auto shrink-0" />
          </Link>
        </div>
      </header>
      <main className="flex-1">
        <form onSubmit={submit} className="max-w-xl w-full mx-auto px-4 py-8 flex flex-col gap-5" noValidate>
          <div>
            <h1 className="font-display text-[26px] leading-tight font-semibold tracking-[-0.02em] text-ink">
              {t('register.enter_invite_title', 'Enter your invite code or paste the link')}
            </h1>
            <p className="mt-2 text-ink-700">
              {t(
                'register.enter_invite_body',
                'Your care team sent it in a text or email. You can paste the whole link, or type just the code.',
              )}
            </p>
          </div>
          <div>
            <label htmlFor="invite-code" className="block font-semibold text-ink mb-1">
              {t('register.invite_code_or_link', 'Invite code or link')}
            </label>
            <input
              id="invite-code"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError('');
              }}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              className={DOOR_INPUT}
            />
          </div>
          {error && (
            <p role="alert" className="rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-danger-ink">
              {error}
            </p>
          )}
          <button type="submit" className={`${DOOR_PRIMARY} w-full`}>
            {t('register.continue', 'Continue')}
          </button>
          <p className="text-center text-ink-700">
            {t('register.no_invite', 'No invite?')}{' '}
            <Link to="/member/join" className="font-semibold text-v2accent-ink underline">
              {t('register.get_help_without', 'Get help without one')}
            </Link>
          </p>
          <HelpLineFooter />
        </form>
      </main>
    </div>
  );
}

type RegistrationStep = 'account' | 'verify_phone' | 'consent' | 'complete';

function MemberRegisterPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  // Invitation state
  const [verifying, setVerifying] = useState(true);
  const [invitationValid, setInvitationValid] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);

  // Multi-step state
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('account');
  const [email, setEmail] = useState('');

  // Step 1: Account creation
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState<string>('email');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Step 2: Phone verification
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  // Step 3: Consent
  const [consentDocuments, setConsentDocuments] = useState<any[]>([]);
  const [termsSignature, setTermsSignature] = useState('');
  const [hipaaSignature, setHipaaSignature] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Verify invitation on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setVerifying(false);
      return;
    }

    verifyInvitation(token)
      .then((data) => {
        setInvitationValid(true);
        setInvitationData(data);
        setEmail(data.email);
      })
      .catch((err) => {
        setError(err.message || 'Invalid or expired invitation');
        setInvitationValid(false);
      })
      .finally(() => {
        setVerifying(false);
      });
  }, [token]);

  // Load consent documents when reaching consent step
  useEffect(() => {
    if (currentStep === 'consent' && consentDocuments.length === 0) {
      getActiveConsentDocuments()
        .then(setConsentDocuments)
        .catch((err) => console.error('Failed to load consent documents:', err));
    }
  }, [currentStep, consentDocuments.length]);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!termsAccepted) {
      setError('Please accept the terms and conditions');
      return;
    }

    if (!phoneNumber || !isValidUsMobile(phoneNumber)) {
      setError('Please provide a valid phone number');
      return;
    }

    setLoading(true);

    try {
      const result = await registerMemberStep1({
        invitationToken: token!,
        password,
        phoneNumber,
        preferredContactMethod: preferredContactMethod as any,
      });

      setPhoneMasked(result.phone_number_masked);
      setCurrentStep('verify_phone');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      await registerVerifyPhone(email, verificationCode);
      setCurrentStep('consent');
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!termsSignature || !hipaaSignature) {
      setError('Please sign both consent documents');
      return;
    }

    setLoading(true);

    try {
      await registerSignConsent(email, termsSignature, hipaaSignature);
      await registerComplete(email);

      // Registration complete!
      setCurrentStep('complete');

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/member/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration. Please try again.');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResendLoading(true);

    try {
      const result = await registerResendCode(email);
      setPhoneMasked(result.phone_number_masked);
      setError(''); // Clear any previous errors
      // Show success message briefly
      setError('Verification code sent successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPhoneNumber || !isValidUsMobile(newPhoneNumber)) {
      setError('Please provide a valid phone number');
      return;
    }

    setLoading(true);

    try {
      const result = await registerUpdatePhone(email, newPhoneNumber);
      setPhoneMasked(result.phone_number_masked);
      setPhoneNumber(newPhoneNumber);
      setNewPhoneNumber('');
      setShowChangePhone(false);
      setVerificationCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to update phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 border-4 border-teal-600 dark:border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t('register.verifying_invitation', 'Verifying invitation...')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitationValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-600 dark:text-red-400">{t('register.invalid_invitation', 'Invalid Invitation')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Link to="/member/login">
              <Button variant="outline">{t('register.go_to_login', 'Go to Login')}</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-2xl">
        {/* Progress Steps */}
        <div className="flex items-center justify-center pt-6 pb-4 px-6">
          <div className="flex items-center space-x-2">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep === 'account' ? 'bg-teal-600 dark:bg-teal-500 text-white' : 'bg-green-600 dark:bg-green-500 text-white'}`}>
              {currentStep === 'account' ? '1' : <CheckCircle className="w-5 h-5" />}
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep === 'verify_phone' ? 'bg-teal-600 text-white' : currentStep === 'consent' || currentStep === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {currentStep === 'consent' || currentStep === 'complete' ? <CheckCircle className="w-5 h-5" /> : '2'}
            </div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep === 'consent' ? 'bg-teal-600 text-white' : currentStep === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {currentStep === 'complete' ? <CheckCircle className="w-5 h-5" /> : '3'}
            </div>
          </div>
        </div>

        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center">
              {currentStep === 'account' && <UserPlus className="w-6 h-6 text-white" />}
              {currentStep === 'verify_phone' && <Phone className="w-6 h-6 text-white" />}
              {currentStep === 'consent' && <Shield className="w-6 h-6 text-white" />}
              {currentStep === 'complete' && <CheckCircle className="w-6 h-6 text-white" />}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {currentStep === 'account' && 'Create Your Account'}
            {currentStep === 'verify_phone' && 'Verify Your Phone'}
            {currentStep === 'consent' && 'Sign Consent Documents'}
            {currentStep === 'complete' && 'Registration Complete!'}
          </CardTitle>
          <CardDescription className="text-center">
            {currentStep === 'account' && `Welcome, ${invitationData?.first_name}! Complete your registration for ${invitationData?.organization}`}
            {currentStep === 'verify_phone' && `Enter the 6-digit code sent to ${phoneMasked}`}
            {currentStep === 'consent' && 'Please review and sign the required consent documents'}
            {currentStep === 'complete' && 'Redirecting you to your dashboard...'}
          </CardDescription>
        </CardHeader>

        {error && (
          <div className="px-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Step 1: Account Creation */}
        {currentStep === 'account' && (
          <form onSubmit={handleStep1Submit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('register.email', 'Email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    value={invitationData?.email || ''}
                    disabled
                    className="pl-10 bg-gray-50 dark:bg-gray-800"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('register.password', 'Password *')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('register.confirm_password', 'Confirm Password *')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder={t('register.confirm_your_password', 'Confirm your password')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Required for verification) *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <PhoneInput
                    id="phone"
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(v) => setPhoneNumber(v)}
                    required
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('register.youll_receive_a_verification_code', 'You\'ll receive a verification code via SMS')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactMethod">{t('register.preferred_contact_method', 'Preferred Contact Method')}</Label>
                <Select
                  value={preferredContactMethod}
                  onValueChange={setPreferredContactMethod}
                  disabled={loading}
                >
                  <SelectTrigger id="contactMethod">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">{t('register.email_1', 'Email')}</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="phone">{t('register.phone', 'Phone')}</SelectItem>
                    <SelectItem value="portal">{t('register.portal_messages', 'Portal Messages')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                  disabled={loading}
                />
                <Label
                  htmlFor="terms"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('register.i_agree_to_receive_the', 'I agree to receive the Terms of Service and HIPAA Authorization for review and signature *')}
                </Label>
              </div>
            </CardContent>

            <CardFooter>
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('register.creating_account', 'Creating account...')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('register.continue_to_phone_verification', 'Continue to Phone Verification')}
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        )}

        {/* Step 2: Phone Verification */}
        {currentStep === 'verify_phone' && (
          <>
            {!showChangePhone ? (
              <form onSubmit={handleStep2Submit}>
                <CardContent className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('register.we_sent_a_6digit_verification', 'We sent a 6-digit verification code to')}<br />
                      <strong>{phoneMasked}</strong>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="code">{t('register.verification_code', 'Verification Code')}</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      disabled={loading}
                      className="text-center text-2xl tracking-widest"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('register.code_expires_in_5_minutes', 'Code expires in 5 minutes')}</p>
                  </div>

                  {/* Resend and Change Phone Options */}
                  <div className="flex flex-col sm:flex-row justify-center gap-2 pt-2 border-t dark:border-gray-700">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResendCode}
                      disabled={resendLoading || loading}
                      className="text-teal-600 hover:text-teal-700 dark:text-teal-400"
                    >
                      {resendLoading ? (
                        <>
                          <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        'Resend Code'
                      )}
                    </Button>
                    <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowChangePhone(true)}
                      disabled={loading}
                      className="text-teal-600 hover:text-teal-700 dark:text-teal-400"
                    >
                      Use Different Phone Number
                    </Button>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-2">
                  <Button
                    type="submit"
                    className="w-full bg-v2accent hover:bg-v2accent/90"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {t('register.verifying', 'Verifying...')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t('register.verify_phone_number', 'Verify Phone Number')}
                      </>
                    )}
                  </Button>
                </CardFooter>
              </form>
            ) : (
              /* Change Phone Number Form */
              <form onSubmit={handleChangePhoneSubmit}>
                <CardContent className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enter a different phone number to receive your verification code.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPhone">New Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <PhoneInput
                        id="newPhone"
                        placeholder="+1 (555) 123-4567"
                        value={newPhoneNumber}
                        onChange={(v) => setNewPhoneNumber(v)}
                        required
                        disabled={loading}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      A new verification code will be sent to this number.
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-2">
                  <Button
                    type="submit"
                    className="w-full bg-v2accent hover:bg-v2accent/90"
                    disabled={loading || !newPhoneNumber}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Phone className="w-4 h-4 mr-2" />
                        Update Phone & Send Code
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowChangePhone(false);
                      setNewPhoneNumber('');
                      setError('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </CardFooter>
              </form>
            )}
          </>
        )}

        {/* Step 3: Consent Signatures */}
        {currentStep === 'consent' && (
          <form onSubmit={handleStep3Submit}>
            <CardContent className="space-y-6">
              {consentDocuments.map((doc) => (
                <div key={doc.id} className="border dark:border-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex items-start space-x-2">
                    <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{doc.title}</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          Version {doc.version}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{doc.summary || 'Please review this document carefully.'}</p>
                      <div className="mt-3 max-h-64 overflow-y-auto bg-gray-50 dark:bg-gray-800 p-4 rounded text-sm border border-gray-200 dark:border-gray-700">
                        <div className="whitespace-pre-wrap">{doc.content}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 pt-3 border-t dark:border-gray-700">
                    <Label htmlFor={`signature-${doc.document_type}`} className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      {t('register.digital_signature_type_your_full', 'Digital Signature - Type your full name to sign *')}
                    </Label>
                    <Input
                      id={`signature-${doc.document_type}`}
                      type="text"
                      placeholder="Your Full Name (e.g., John Doe)"
                      value={doc.document_type === 'terms_of_use' ? termsSignature : hipaaSignature}
                      onChange={(e) => {
                        if (doc.document_type === 'terms_of_use') {
                          setTermsSignature(e.target.value);
                        } else {
                          setHipaaSignature(e.target.value);
                        }
                      }}
                      required
                      disabled={loading}
                      className="font-serif italic text-lg"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('register.by_typing_your_name_you', 'By typing your name, you agree to be legally bound by this document')}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>

            <CardFooter>
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading || !termsSignature || !hipaaSignature}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('register.completing_registration', 'Completing registration...')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('register.sign_documents_complete_registration', 'Sign Documents & Complete Registration')}
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        )}

        {/* Step 4: Complete */}
        {currentStep === 'complete' && (
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('register.welcome_to_trust_marketplace', 'Welcome to Trust Marketplace!')}</h3>
            <p className="text-gray-600 dark:text-gray-400">{t('register.your_account_has_been_successfully', 'Your account has been successfully created.')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">{t('register.redirecting_to_your_dashboard', 'Redirecting to your dashboard...')}</p>
            <div className="w-12 h-12 border-4 border-teal-600 dark:border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mt-4" />
          </CardContent>
        )}
      </Card>

      <div className="fixed bottom-4 right-4 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
